import { useMemo } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusDot } from "@/components/StatusPill";
import { indexByDate, monthGrid, toISO, worstStatus, MONTHS_NN, WEEKDAYS_MIN_NN, yearSummary, dayTitleLong } from "@/lib/calendar";
import { cn } from "@/lib/utils";

const MiniMonth = ({ year, month, idx, today, onPickDay, onPickMonth, stats }) => {
  const weeks = useMemo(() => monthGrid(new Date(year, month, 1)), [year, month]);
  const peak = stats.total >= 4;
  return (
    <div className="rounded-2xl border bg-card p-3 shadow-sm" data-testid={`calendar-year-month-${month + 1}`}>
      <button
        type="button"
        onClick={() => onPickMonth(month)}
        className="flex w-full items-center justify-between rounded-lg px-1 py-1 text-left transition-colors hover:bg-accent"
        data-testid={`calendar-year-month-title-${month + 1}`}
      >
        <span className="font-heading text-sm font-bold">{MONTHS_NN[month]}</span>
        <span className="flex items-center gap-1.5 text-[11px] font-semibold tabular-nums">
          {stats.overdue > 0 && <span className="status-overdue rounded-full border px-1.5 py-px">{stats.overdue}</span>}
          {stats.upcoming > 0 && <span className="status-upcoming rounded-full border px-1.5 py-px">{stats.upcoming}</span>}
          {stats.ok > 0 && <span className="status-ok rounded-full border px-1.5 py-px">{stats.ok}</span>}
          {stats.planned > 0 && <span className="status-planned rounded-full border px-1.5 py-px">{stats.planned}</span>}
          {stats.total === 0 && stats.planned === 0 && <span className="text-muted-foreground">–</span>}
        </span>
      </button>
      {peak && <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-primary">Travel månad</div>}
      <div className="mt-1 grid grid-cols-7 justify-items-center gap-1 px-0.5">
        {WEEKDAYS_MIN_NN.map((d, i) => (
          <div key={i} className="w-5 text-center text-[9px] font-semibold text-muted-foreground/70 sm:w-[22px]">{d}</div>
        ))}
        {weeks.flat().map((day) => {
          const iso = toISO(day);
          const inMonth = day.getMonth() === month;
          const due = idx.due.get(iso) || [];
          const planned = idx.planned.get(iso) || [];
          const status = worstStatus(due.map((e) => e.status));
          const isToday = iso === today;
          const has = inMonth && (status || planned.length > 0);
          const cell = (
            <button
              type="button"
              onClick={() => onPickDay(iso)}
              disabled={!inMonth}
              aria-label={`${dayTitleLong(day)}${due.length ? `, ${due.length} fristar` : ""}${planned.length ? `, ${planned.length} planlagt` : ""}`}
              data-testid={has ? `calendar-year-day-${iso}` : undefined}
              className={cn(
                "relative h-5 w-5 rounded-[4px] transition-transform duration-150 sm:h-[22px] sm:w-[22px]",
                !inMonth && "opacity-0",
                inMonth && !status && planned.length === 0 && "bg-muted/70",
                status && `cal-fill-${status}`,
                !status && planned.length > 0 && "cal-fill-planned",
                has && "hover:scale-110",
                isToday && "ring-2 ring-primary ring-offset-1 ring-offset-card"
              )}
            >
              {status && planned.length > 0 && <span className="absolute inset-x-0 bottom-0 h-[3px] rounded-b-[4px] bg-[rgb(var(--status-planned))]" />}
            </button>
          );
          if (!has) return <div key={iso}>{cell}</div>;
          return (
            <Tooltip key={iso} delayDuration={150}>
              <TooltipTrigger asChild>{cell}</TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] text-xs">
                <div className="font-semibold">{dayTitleLong(day)}</div>
                <ul className="mt-1 space-y-0.5">
                  {due.map((e) => (
                    <li key={e.id} className="flex items-center gap-1.5"><StatusDot status={e.status} className="h-2 w-2" /> {e.label} · {e.kind === "annual" ? "Årskontroll" : "Service"}</li>
                  ))}
                  {planned.map((e) => (
                    <li key={`${e.id}-p`} className="flex items-center gap-1.5"><span className="cal-fill-planned inline-block h-2 w-2 rounded-full" /> Planlagt: {e.label}</li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export const YearView = ({ year, events, today, onPickDay, onPickMonth }) => {
  const idx = useMemo(() => indexByDate(events), [events]);
  const stats = useMemo(() => yearSummary(events, year), [events, year]);
  const totals = stats.reduce(
    (acc, m) => ({ overdue: acc.overdue + m.overdue, upcoming: acc.upcoming + m.upcoming, ok: acc.ok + m.ok, planned: acc.planned + m.planned, total: acc.total + m.total }),
    { overdue: 0, upcoming: 0, ok: 0, planned: 0, total: 0 }
  );
  const busiest = stats.reduce((best, m, i) => (m.total > (stats[best]?.total ?? -1) ? i : best), 0);

  return (
    <TooltipProvider>
      <div className="space-y-4" data-testid="calendar-year-view">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border bg-card px-4 py-3 text-sm shadow-sm" data-testid="calendar-year-summary">
          <div><span className="font-heading text-2xl font-extrabold tabular-nums">{totals.total}</span> <span className="text-muted-foreground">fristar i {year}</span></div>
          <div className="flex items-center gap-1.5 text-xs"><StatusDot status="overdue" /> <b className="tabular-nums">{totals.overdue}</b> forfalt</div>
          <div className="flex items-center gap-1.5 text-xs"><StatusDot status="upcoming" /> <b className="tabular-nums">{totals.upcoming}</b> nærmar seg</div>
          <div className="flex items-center gap-1.5 text-xs"><StatusDot status="ok" /> <b className="tabular-nums">{totals.ok}</b> OK</div>
          <div className="flex items-center gap-1.5 text-xs"><span className="cal-fill-planned inline-block h-2.5 w-2.5 rounded-full" /> <b className="tabular-nums">{totals.planned}</b> planlagt</div>
          {totals.total > 0 && (
            <div className="ml-auto text-xs text-muted-foreground">Travlast: <b className="text-foreground">{MONTHS_NN[busiest]}</b> ({stats[busiest].total})</div>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 12 }, (_, m) => (
            <MiniMonth key={m} year={year} month={m} idx={idx} today={today} stats={stats[m]} onPickDay={onPickDay} onPickMonth={onPickMonth} />
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
};
